import { useMemo } from "react";
import useCompletedByItemId from "@/hooks/use-completed-by-item-id";
import useCurrentModuleItemId from "./use-current-module-item-id";
import trpc, { TrpcOutput } from "@/utils/trpc";
import { buildItemLink } from "@/utils/canvas";

export interface ModuleItemInfo {
  id: string;
  to: string;
  hasRequirement: boolean;
  completed: boolean;
}

interface LearningState {
  targetCTA: string | null;
  hasStarted: boolean;
  prev: ModuleItemInfo | null;
  next: ModuleItemInfo | null;
  hasRequirements: boolean;
  progressPercent: number; // 0-100 integer
}

export function extractModuleItems(
  modules: TrpcOutput["course"]["getModules"],
  completed: Set<string>,
): ModuleItemInfo[] {
  if (!modules) return [];
  const items: ModuleItemInfo[] = [];
  for (const mod of modules) {
    for (const sub of mod.subModules) {
      for (const item of sub.items) {
        const to = buildItemLink(item);
        if (!to) continue;
        const id = String(item.id);
        items.push({
          id,
          to,
          hasRequirement: !!item.completion_requirement,
          completed: completed.has(id),
        });
      }
    }
  }
  return items;
}

export function deriveProgress(items: ModuleItemInfo[]) {
  const required = items.filter(i => i.hasRequirement);
  const completedRequiredCount = required.filter(r => r.completed).length;
  const totalRequiredCount = required.length;
  const hasStarted = completedRequiredCount > 0;
  const progressPercent = totalRequiredCount > 0 ? Math.round((completedRequiredCount / totalRequiredCount) * 100) : 0;
  return { required, totalRequiredCount, hasStarted, progressPercent };
}

export function deriveTargetCTA(required: ModuleItemInfo[]) {
  for (const r of required) {
    if (!r.completed) return r.to;
  }
  return null;
}

export function deriveNavigation(items: ModuleItemInfo[], currentId?: string | null) {
  let prev: ModuleItemInfo | null = null;
  let next: ModuleItemInfo | null = null;
  if (currentId) {
    const idx = items.findIndex(i => i.id === currentId);
    if (idx !== -1) {
      prev = idx > 0 ? items[idx - 1] : null;
      next = idx < items.length - 1 ? items[idx + 1] : null;
    }
  }
  return { prev, next };
}

export default function useLearningState(): LearningState {
  const { data: modules } = trpc.course.getModules.useQuery(undefined, { staleTime: Infinity });
  const { completedByItemId } = useCompletedByItemId();
  const currentModuleItemId = useCurrentModuleItemId();

  return useMemo(() => {
    if (!modules)
      return {
        targetCTA: null,
        hasStarted: false,
        prev: null,
        next: null,
        hasRequirements: false,
        progressPercent: 0,
      };

    const moduleItems = extractModuleItems(modules, completedByItemId);
    const { required, totalRequiredCount, hasStarted, progressPercent } = deriveProgress(moduleItems);
    const targetCTA = deriveTargetCTA(required);
    const { prev, next } = deriveNavigation(moduleItems, currentModuleItemId);

    return {
      targetCTA,
      hasStarted,
      prev,
      next,
      hasRequirements: totalRequiredCount > 0,
      progressPercent,
    };
  }, [modules, completedByItemId, currentModuleItemId]);
}
